import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { Language } from '@/types';
import {
  getStoredLanguage,
  saveLanguageToStorage,
} from '@/utils/storageUtils';

interface AppContextType {
  language: Language;
  setLanguage: (language: Language) => void;
  isChangingLanguage: boolean;
  setIsChangingLanguage: (changing: boolean) => void;
  previousLanguage: Language;
  setPreviousLanguage: (lang: Language) => void;
  scrollDirection: 'up' | 'down' | null;
  setScrollContainer: (element: HTMLElement | null) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);
interface AppProviderProps {
  children: ReactNode;
}

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [language, setLanguage] = useState<Language>(getStoredLanguage);
  const [isChangingLanguage, setIsChangingLanguage] = useState(false);
  const [previousLanguage, setPreviousLanguage] =
    useState<Language>(getStoredLanguage);
  const [scrollDirection, setScrollDirection] = useState<'up' | 'down' | null>(
    null,
  );
  const [scrollContainer, setScrollContainer] = useState<HTMLElement | null>(
    null,
  );

  const updateLanguage = (newLanguage: Language) => {
    setLanguage(newLanguage);
    saveLanguageToStorage(newLanguage);
  };

  useEffect(() => {
    if (!scrollContainer) return;

    let lastScrollY = scrollContainer.scrollTop;
    let ticking = false;
    const threshold = 15;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const currentScrollY = scrollContainer.scrollTop;

          const diff = currentScrollY - lastScrollY;
          if (Math.abs(diff) > threshold) {
            setScrollDirection(diff > 0 ? 'down' : 'up');
          }
          lastScrollY = currentScrollY;
          ticking = false;
        });
        ticking = true;
      }
    };

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [scrollContainer]);

  const value: AppContextType = {
    language,
    setLanguage: updateLanguage,
    isChangingLanguage,
    previousLanguage,
    setIsChangingLanguage,
    setPreviousLanguage,
    scrollDirection,
    setScrollContainer,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
